import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Field } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { Eyebrow } from '@/components/forge/primitives'
import { MAX_PAGE_SIZE } from '@/features/sensors/api'
import { useSensors } from '@/features/sensors/queries'

/**
 * The signals an entry pins a time window to.
 *
 * `sensor_tags` on `LogBookEntryCreate` — `{sensor_tag_id, window_start,
 * window_end}` per row. What it means: "read this signal across these minutes
 * and you will see what I wrote about". It is the one field that turns a note
 * from prose into evidence, which is why it is offered rather than sent empty.
 *
 * ── The same tag may repeat ──────────────────────────────────────────────
 * Identity server-side is `(sensor_tag_id, window_start, window_end)`, stated
 * in `update_logbook_entry_action` — so one signal across two separate windows
 * is two rows and not a mistake. Nothing here deduplicates by tag.
 *
 * ── It needs a permission the logbook does not imply ─────────────────────
 * The only way to enumerate tags is `GET /v1/sensors`, which is behind
 * `sensors.manage_sensors`. `logbook.record_entries` does not include it, and
 * an operator writing up a night shift is exactly the person likely to hold the
 * second and not the first.
 *
 * So the field is offered only when the list is readable, and its absence is
 * SAID rather than drawn as an empty select — which reads as "this machine has
 * no sensors", a different and wronger claim. Same call `WorkOrderFormDialog`
 * makes about the anomaly picker.
 *
 * ── It also needs a machine ──────────────────────────────────────────────
 * Tags hang off sensors and sensors hang off assets, so there is nothing to
 * offer until the entry has a parent — and a plant- or line-level entry has no
 * machine to read them from. Both states say so rather than showing a control
 * that cannot be filled.
 */
export function LogbookSensorTagField({ machineId, rows, onChange, canRead, disabled }) {
  const { t } = useTranslation(['app', 'common'])

  const sensors = useSensors({
    machineId,
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    enabled: canRead && Boolean(machineId),
  })

  /**
   * Every tag on the machine, flattened across its devices.
   *
   * Labelled `device · tag` because a tag's own `name` is nullable and often
   * terse — two devices on one machine may both carry a tag called `temp`, and
   * a select offering it twice is unusable. The device name is what
   * distinguishes them.
   */
  const tags = useMemo(
    () =>
      (sensors.data?.items ?? []).flatMap((sensor) =>
        sensor.tags.map((tag) => ({
          id: tag.id,
          label: tag.name ? `${sensor.name} · ${tag.name}` : sensor.name,
        })),
      ),
    [sensors.data],
  )

  const set = (index, key, value) =>
    onChange((current) =>
      current.map((row, position) => (position === index ? { ...row, [key]: value } : row)),
    )

  const add = () =>
    onChange((current) => [
      ...current,
      /* An hour back from now, which is the window somebody writing up what
         they just saw actually means. Both ends are editable and neither is a
         guess the form hides. */
      { sensorTagId: '', windowStart: localNow(-60), windowEnd: localNow(0) },
    ])

  const remove = (index) =>
    onChange((current) => current.filter((_, position) => position !== index))

  return (
    <div className="flex flex-col gap-2">
      <Eyebrow>{t('logbook.form.sensorTags')}</Eyebrow>

      {!canRead ? (
        /* On an edit the pinned windows still travel — the `PUT` is a full
           replace — so a reader who cannot see them is told they are kept. */
        <p className="m-0 text-body-sm text-ink-dim">
          {rows.length
            ? t('logbook.form.sensorTagsKept', { count: rows.length })
            : t('logbook.form.sensorTagsNoPermission')}
        </p>
      ) : !machineId ? (
        <p className="m-0 text-body-sm text-ink-dim">{t('logbook.form.sensorTagsNeedMachine')}</p>
      ) : sensors.isError ? (
        /* Said, not drawn as an empty select. An unreadable list and a machine
           with no sensors look identical in a dropdown and mean opposite
           things. The entry still saves — the field is optional. */
        <p className="m-0 text-body-sm text-on-amber">{t('logbook.form.sensorTagsFailed')}</p>
      ) : (
        <>
          <p className="m-0 text-body-sm text-ink-dim">{t('logbook.form.sensorTagsHint')}</p>

          {rows.map((row, index) => {
            const invalid = Boolean(row.windowStart && row.windowEnd) && row.windowEnd <= row.windowStart

            return (
              <div
                key={index}
                className="flex flex-col gap-2.5 rounded-sm border border-hairline bg-sunken p-3"
              >
                <div className="flex items-end gap-2">
                  <ForgeSelect
                    className="min-w-0 flex-1"
                    label={t('logbook.form.sensorTag')}
                    size="sm"
                    value={row.sensorTagId}
                    onValueChange={(value) => set(index, 'sensorTagId', value)}
                    placeholder={
                      sensors.isPending
                        ? t('common:loading')
                        : t('logbook.form.sensorTagPlaceholder')
                    }
                    disabled={disabled || sensors.isPending}
                    options={tags.map((tag) => ({ value: tag.id, label: tag.label }))}
                  />
                  <ForgeButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => remove(index)}
                    aria-label={t('logbook.form.sensorTagRemove')}
                  >
                    <Icon name="trash" size="xs" />
                  </ForgeButton>
                </div>

                {/* `datetime-local`, and the value is converted before it is
                    sent — `NaiveUTCDatetime` refuses a zone marker and the
                    browser gives a LOCAL wall clock with none. See `toNaiveUtc`
                    in features/logbook/api.js; skipping it stores the right
                    string and the wrong instant. */}
                <div className="grid gap-2.5 sm:grid-cols-2">
                  <Field
                    label={t('logbook.form.windowStart')}
                    type="datetime-local"
                    value={row.windowStart}
                    disabled={disabled}
                    onChange={(event) => set(index, 'windowStart', event.target.value)}
                  />
                  <Field
                    label={t('logbook.form.windowEnd')}
                    type="datetime-local"
                    value={row.windowEnd}
                    disabled={disabled}
                    onChange={(event) => set(index, 'windowEnd', event.target.value)}
                    /* The server stores a backwards window without complaint —
                       there is no validator on the pair. So this check is this
                       client's alone, and it is recorded in BACKEND-GAPS.md
                       because a rule only the browser enforces is not a rule. */
                    error={invalid ? t('logbook.form.windowBackwards') : undefined}
                  />
                </div>
              </div>
            )
          })}

          {tags.length === 0 && !sensors.isPending ? (
            <p className="m-0 text-body-sm text-ink-dim">{t('logbook.form.sensorTagsNone')}</p>
          ) : (
            <div>
              <ForgeButton type="button" size="sm" disabled={disabled} onClick={add}>
                <Icon name="plus" size="xs" />
                {t('logbook.form.sensorTagAdd')}
              </ForgeButton>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Now, offset by minutes, as the `YYYY-MM-DDTHH:MM` a `datetime-local` wants.
 *
 * Local on purpose: this is the control's value, not the wire's. The shift to
 * UTC happens once, at the edge, in `toNaiveUtc`.
 */
function localNow(offsetMinutes) {
  const now = new Date(Date.now() + offsetMinutes * 60_000)
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/** Whether every row is complete and its window runs forwards. */
export const sensorTagsValid = (rows) =>
  rows.every(
    (row) => row.sensorTagId && row.windowStart && row.windowEnd && row.windowEnd > row.windowStart,
  )
